'use client'

import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { DeleteIcon } from '@repo/ui/DeleteIcon'
import { EditIcon } from '@repo/ui/EditIcon'
import { formatCurrency } from '@repo/utils/numbers'
import { HoverActionsPill } from '@repo/ui/HoverActionsPill'
import { PaperClipIcon } from '@repo/ui/PaperClipIcon'
import MUIBox from '@mui/material/Box'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export interface DraftClaim {
  expenseClaimId: string
  incurredDate: string
  clientNo: string
  matterNo: string
  expenseClaimTypeDescription: string
  description: string
  receiptFileSize?: number
  grossAmount: number
  amount: number
}

interface DraftClaimCardProps {
  claim: DraftClaim
  onEdit?: (claim: DraftClaim) => void
  onDelete?: (claimId: string) => void
}

export const DraftClaimCard = ({ claim, onEdit, onDelete }: DraftClaimCardProps) => {
  const formattedDate = claim.incurredDate
    ? new Date(claim.incurredDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : ''

  return (
    <MUIBox
      data-testid="DraftClaimCard"
      sx={{
        position: 'relative',
        backgroundColor: '#F8F9FB',
        borderRadius: '6px',
        padding: '16px',
        '&:hover': {
          backgroundColor: '#F0F2F5',
          '& .hoverActionsPill': {
            opacity: 1,
            visibility: 'visible'
          }
        }
      }}
    >
      <MUIStack direction="row" justifyContent="space-between" alignItems="center" mb={1} sx={{ position: 'relative' }}>
        <MUIStack direction="column" spacing={1}>
          <MUIStack direction="row" alignItems="center" justifyContent="space-between">
            <MUIStack direction="row" alignItems="center" spacing={2} sx={{ minWidth: '80%' }}>
              <MUITypography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: '#051C33'
                }}
              >
                {formattedDate}
              </MUITypography>
              <ClientMatterDetailsPill
                clientNo={claim.clientNo}
                matterNo={claim.matterNo}
                sx={{ position: 'relative', top: '2px', flexShrink: 0 }}
              />
              <MUITypography
                variant="body1"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0
                }}
              >
                {claim.expenseClaimTypeDescription}
              </MUITypography>
            </MUIStack>
          </MUIStack>

          {claim.description && (
            <MUITypography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 8,
                maxWidth: '80%'
              }}
            >
              {claim.description}
            </MUITypography>
          )}
        </MUIStack>
        <MUIStack direction="row" alignItems="center" gap={6}>
          {claim.receiptFileSize && claim.receiptFileSize > 0 && (
            <PaperClipIcon sx={{ width: 16, height: 16, color: '#6C757D', position: 'relative', top: '0px' }} />
          )}
          <MUITypography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: '#051C33',
              whiteSpace: 'nowrap',
              mb: 0
            }}
          >
            {formatCurrency(claim.grossAmount ?? claim.amount)}
          </MUITypography>
        </MUIStack>
        <HoverActionsPill top="12%" right="-4px" padding="4px 8px" height="36px">
          <MUITooltip title="Edit claim" arrow placement="top">
            <MUIIconButton
              size="small"
              onClick={() => onEdit?.(claim)}
              sx={{ '&:hover': { backgroundColor: 'rgba(80, 115, 195, 0.1)' } }}
            >
              <EditIcon sx={{ width: 18, height: 18 }} />
            </MUIIconButton>
          </MUITooltip>
          <MUITooltip title="Remove claim" arrow placement="top">
            <MUIIconButton
              size="small"
              onClick={() => onDelete?.(claim.expenseClaimId)}
              sx={{ '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
            >
              <DeleteIcon sx={{ width: 18, height: 18 }} />
            </MUIIconButton>
          </MUITooltip>
        </HoverActionsPill>
      </MUIStack>
    </MUIBox>
  )
}
